import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import {
  Briefcase,
  Loader2,
  RefreshCcw,
  Search,
  Mail,
  Phone,
  ExternalLink,
  ChevronDown,
  Lock,
  LogOut,
} from "lucide-react";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { useAuth } from "../lib/AuthContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Row = ({ app, expanded, onToggle }) => {
  const created = app.created_at ? new Date(app.created_at) : null;
  return (
    <div
      data-testid={`admin-emp-row-${app.id}`}
      className="border-b border-neutral-900"
    >
      <button
        type="button"
        onClick={onToggle}
        className="w-full grid grid-cols-12 gap-4 px-5 py-4 text-left hover:bg-[#0a0a0a] transition-colors"
      >
        <div className="col-span-12 md:col-span-3">
          <div className="text-sm text-white">
            {app.first_name} {app.last_name}
          </div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-neutral-500 mt-1">
            {created ? created.toLocaleDateString() : "—"}
          </div>
        </div>
        <div className="col-span-12 md:col-span-4">
          <div className="text-sm text-white truncate">{app.position}</div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-amber-400 mt-1">
            {app.track}
          </div>
        </div>
        <div className="col-span-12 md:col-span-4 min-w-0">
          <div className="text-sm text-neutral-300 truncate">{app.email}</div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-neutral-500 mt-1">
            {app.phone}
          </div>
        </div>
        <div className="hidden md:flex col-span-1 items-center justify-end">
          <ChevronDown
            size={16}
            className={`text-neutral-500 transition-transform ${expanded ? "rotate-180" : ""}`}
          />
        </div>
      </button>

      {expanded && (
        <div className="px-5 pb-6 pt-2 bg-black grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-3 text-xs">
            <div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-neutral-500">Location</div>
              <div className="text-neutral-200">{app.location || "—"}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-neutral-500">LinkedIn</div>
              {app.linkedin ? (
                <a
                  href={app.linkedin}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-amber-300 hover:text-amber-400 inline-flex items-center gap-1.5"
                >
                  Open profile <ExternalLink size={11} />
                </a>
              ) : (
                <div className="text-neutral-500">—</div>
              )}
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-neutral-500">Portfolio</div>
              {app.portfolio ? (
                <a
                  href={app.portfolio}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-amber-300 hover:text-amber-400 inline-flex items-center gap-1.5"
                >
                  Open portfolio <ExternalLink size={11} />
                </a>
              ) : (
                <div className="text-neutral-500">—</div>
              )}
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-neutral-500">Resume</div>
              {app.resume_link ? (
                <a
                  href={app.resume_link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-amber-300 hover:text-amber-400 inline-flex items-center gap-1.5"
                >
                  Open resume <ExternalLink size={11} />
                </a>
              ) : (
                <div className="text-neutral-500">—</div>
              )}
            </div>
            <div className="flex flex-wrap gap-3 pt-3">
              <a
                href={`mailto:${app.email}?subject=Your%20application%20for%20${encodeURIComponent(app.position)}`}
                className="inline-flex items-center gap-2 border border-amber-400/40 text-amber-300 px-4 py-2 text-[10px] uppercase tracking-[0.28em] font-bold hover:bg-amber-400 hover:text-black transition-colors"
              >
                <Mail size={12} /> Email
              </a>
              <a
                href={`tel:${app.phone}`}
                className="inline-flex items-center gap-2 border border-neutral-800 text-neutral-300 px-4 py-2 text-[10px] uppercase tracking-[0.28em] font-bold hover:bg-white hover:text-black transition-colors"
              >
                <Phone size={12} /> Call
              </a>
            </div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-neutral-500 mb-2">
              Cover Letter
            </div>
            <p className="text-xs text-neutral-200 leading-relaxed whitespace-pre-wrap">
              {app.cover_letter || "—"}
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

const AdminEmploymentPage = () => {
  const { user, loading: authLoading, signIn, signOut } = useAuth();
  const isAuthed = !!user?.is_admin;

  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [query, setQuery] = useState("");
  const [trackFilter, setTrackFilter] = useState("All");
  const [expandedId, setExpandedId] = useState(null);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await axios.get(`${API}/admin/employment`);
      setApps(Array.isArray(data) ? data : []);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 401 || status === 403) {
        setApps([]);
      } else {
        const detail = err?.response?.data?.detail || "Failed to load applications.";
        setError(typeof detail === "string" ? detail : "Failed to load.");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isAuthed) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/admin/employment`);
        if (cancelled) return;
        setApps(Array.isArray(data) ? data : []);
      } catch (err) {
        if (cancelled) return;
        const status = err?.response?.status;
        if (status === 401 || status === 403) {
          setApps([]);
        } else {
          const detail = err?.response?.data?.detail || "Failed to load applications.";
          setError(typeof detail === "string" ? detail : "Failed to load.");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isAuthed]);

  const tracks = useMemo(() => {
    const s = new Set(apps.map((a) => a.track).filter(Boolean));
    return ["All", ...Array.from(s)];
  }, [apps]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return apps.filter((a) => {
      if (trackFilter !== "All" && a.track !== trackFilter) return false;
      if (!q) return true;
      const blob = `${a.first_name} ${a.last_name} ${a.email} ${a.position} ${a.phone}`.toLowerCase();
      return blob.includes(q);
    });
  }, [apps, query, trackFilter]);

  if (authLoading) {
    return (
      <div className="App bg-black text-white min-h-screen">
        <Navbar />
        <section className="pt-32 md:pt-40 pb-24 min-h-[80vh] flex items-center justify-center">
          <Loader2 size={28} className="text-amber-400 animate-spin" />
        </section>
        <Footer />
      </div>
    );
  }

  if (!isAuthed) {
    return (
      <div className="App bg-black text-white min-h-screen">
        <Navbar />
        <section className="pt-32 md:pt-40 pb-24 min-h-[80vh]">
          <div className="max-w-[820px] mx-auto px-6 md:px-12">
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              data-testid="admin-emp-signin"
              className="border border-amber-400/30 bg-[#0a0a0a] p-8 md:p-12"
            >
              <div className="text-[10px] uppercase tracking-[0.4em] text-amber-400 mb-5">
                — Admin · Restricted
              </div>
              <Lock size={42} className="text-amber-400 mb-6" />
              <h1 className="font-display uppercase text-4xl md:text-6xl text-white leading-[0.9]">
                Admin
                <br />
                <span className="text-amber-400">access only.</span>
              </h1>
              <p className="text-neutral-300 mt-6 max-w-xl leading-relaxed">
                Sign in with your AfroAm-allowlisted Google account to view employment
                applications.
                {user && !user.is_admin ? (
                  <>
                    <br />
                    <span className="text-red-400">
                      Signed in as <strong>{user.email}</strong> — not on the allowlist.
                    </span>
                  </>
                ) : null}
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <button
                  onClick={signIn}
                  data-testid="admin-emp-google-signin"
                  className="inline-flex items-center gap-3 bg-amber-400 text-black px-6 py-3.5 text-xs uppercase tracking-[0.28em] font-bold hover:bg-amber-300 transition-colors"
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" aria-hidden="true">
                    <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                    <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.99.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                    <path fill="currentColor" d="M5.84 14.1A6.96 6.96 0 0 1 5.5 12c0-.73.13-1.45.34-2.1V7.06H2.18A11 11 0 0 0 1 12c0 1.77.42 3.45 1.18 4.94l3.66-2.84z" />
                    <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z" />
                  </svg>
                  Sign in with Google
                </button>
                {user && (
                  <button
                    onClick={signOut}
                    data-testid="admin-emp-signout-fallback"
                    className="inline-flex items-center gap-2 border border-neutral-800 text-neutral-300 px-6 py-3.5 text-xs uppercase tracking-[0.28em] font-bold hover:bg-white hover:text-black transition-colors"
                  >
                    <LogOut size={14} /> Sign out
                  </button>
                )}
              </div>
              <p className="text-[10px] uppercase tracking-[0.25em] text-neutral-500 mt-8">
                Need access? Email <span className="text-amber-300">afroambasketball@gmail.com</span>
              </p>
            </motion.div>
          </div>
        </section>
        <Footer />
      </div>
    );
  }

  return (
    <div className="App bg-black text-white min-h-screen">
      <Navbar />

      <section className="pt-32 md:pt-40 pb-12 border-b border-neutral-900 bg-gradient-to-b from-[#0a0a0a] to-black">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12">
          <div className="flex items-center gap-3 text-[10px] uppercase tracking-[0.45em] text-amber-400 mb-6">
            <span className="w-8 h-px bg-amber-400" />
            <Briefcase size={14} />
            <span>Admin · Employment Inbox</span>
          </div>
          <motion.h1
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            data-testid="admin-emp-title"
            className="font-display uppercase text-5xl md:text-7xl text-white leading-[0.88]"
          >
            Applications <span className="text-amber-400">inbox.</span>
          </motion.h1>
          <p className="text-neutral-400 max-w-xl mt-6 font-light leading-relaxed">
            All employment + internship applications submitted via{" "}
            <span className="text-white">/employment</span>. Newest first.
          </p>
        </div>
      </section>

      <section className="py-10 md:py-12 border-b border-neutral-900">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12">
          {/* Toolbar */}
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
            <div className="flex flex-wrap items-center gap-3">
              <div className="relative">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500" />
                <input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  data-testid="admin-emp-search"
                  placeholder="Search name, email, position..."
                  className="bg-transparent border border-neutral-800 focus:border-amber-400 text-white text-sm pl-9 pr-4 py-2.5 outline-none placeholder:text-neutral-700 min-w-[260px]"
                />
              </div>
              <select
                value={trackFilter}
                onChange={(e) => setTrackFilter(e.target.value)}
                data-testid="admin-emp-track-filter"
                className="bg-transparent border border-neutral-800 focus:border-amber-400 text-white text-sm px-3 py-2.5 outline-none [color-scheme:dark]"
              >
                {tracks.map((t) => (
                  <option key={t}>{t}</option>
                ))}
              </select>
            </div>
            <div className="flex items-center gap-4 flex-wrap">
              <div className="text-[10px] uppercase tracking-[0.3em] text-neutral-500" data-testid="admin-emp-count">
                {filtered.length} / {apps.length} applications
              </div>
              <button
                onClick={fetchData}
                data-testid="admin-emp-refresh"
                className="inline-flex items-center gap-2 border border-neutral-800 hover:border-amber-400 text-neutral-300 hover:text-amber-300 px-4 py-2.5 text-[10px] uppercase tracking-[0.28em] font-bold transition-colors"
              >
                <RefreshCcw size={12} /> Refresh
              </button>
              <div className="flex items-center gap-2 border border-neutral-900 pl-2 pr-1 py-1.5" data-testid="admin-emp-user-chip">
                {user?.picture ? (
                  <img src={user.picture} alt="" className="w-6 h-6 rounded-full" />
                ) : (
                  <div className="w-6 h-6 rounded-full bg-amber-400/20 text-amber-300 text-[10px] font-bold flex items-center justify-center">
                    {(user?.name || user?.email || "?").slice(0, 1).toUpperCase()}
                  </div>
                )}
                <div className="text-[10px] uppercase tracking-[0.2em] text-neutral-400">
                  {user?.email}
                </div>
                <button
                  onClick={signOut}
                  data-testid="admin-emp-signout"
                  aria-label="Sign out"
                  className="text-neutral-500 hover:text-amber-300 p-1.5"
                >
                  <LogOut size={12} />
                </button>
              </div>
            </div>
          </div>

          {/* List */}
          <div className="border border-neutral-900 bg-[#070707]" data-testid="admin-emp-list">
            <div className="hidden md:grid grid-cols-12 gap-4 px-5 py-3 text-[10px] uppercase tracking-[0.3em] text-neutral-500 border-b border-neutral-900 bg-black">
              <div className="col-span-3">Applicant</div>
              <div className="col-span-4">Position · Track</div>
              <div className="col-span-4">Contact</div>
              <div className="col-span-1 text-right">Details</div>
            </div>

            {loading ? (
              <div className="px-5 py-16 text-center text-neutral-500">
                <Loader2 size={20} className="animate-spin mx-auto mb-3" />
                <div className="text-[10px] uppercase tracking-[0.3em]">Loading applications</div>
              </div>
            ) : error ? (
              <div className="px-5 py-12 text-center text-red-400 text-sm" data-testid="admin-emp-error">
                {error}
              </div>
            ) : filtered.length === 0 ? (
              <div className="px-5 py-16 text-center text-neutral-500" data-testid="admin-emp-empty">
                <Briefcase size={20} className="mx-auto mb-3 text-neutral-700" />
                <div className="text-[10px] uppercase tracking-[0.3em]">
                  {apps.length === 0 ? "No applications yet" : "No applications match your filter"}
                </div>
              </div>
            ) : (
              filtered.map((app) => (
                <Row
                  key={app.id}
                  app={app}
                  expanded={expandedId === app.id}
                  onToggle={() => setExpandedId(expandedId === app.id ? null : app.id)}
                />
              ))
            )}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AdminEmploymentPage;
